import type { AxonEvent } from '@reflex/shared';
import type {
  ToolCallBlock,
  TaskBlock,
  PlanEntry,
  ClaudeInitExtensions,
  ControlRequestBlock,
  ChatItem,
  ContentItem,
  DiffContent,
} from './types.js';
import type { BlockManager } from './useBlockManager.js';
import { nextBlockId, inferToolKind, durationSecs, UNTITLED_TOOL_CALL } from './parsers.js';
import { cancelInFlightTools } from './cancellation.js';
import { claudeAssistantModel } from './active-model.js';

/**
 * Handle a Claude protocol event payload.
 * Modeled after the combined-app's handleSDKMessage dispatch.
 */
export function handleClaudeProtocolEvent(
  data: Record<string, unknown>,
  blocks: BlockManager,
  appendMessage: (msg: ChatItem) => void,
  setIsAgentTurn: (v: boolean) => void,
  eventTimestamp: number,
  eventOrigin?: string,
  sourceEvent?: AxonEvent,
  // True when the user has interrupted the current turn (`agent.interrupted`
  // already seen). Claude reports an interrupted turn as a `result` with
  // subtype `error_during_execution` / `is_error: true`, which is
  // indistinguishable from a genuine crash — so the turn-level "cancelled"
  // decision is driven by this flag, not the result payload.
  interrupted = false,
): void {
  const msgType = data.type as string;

  switch (msgType) {
    case 'stream_event': {
      const event = data.event as Record<string, unknown>;
      if (event) handleStreamEvent(event, blocks, setIsAgentTurn, eventTimestamp, sourceEvent);
      break;
    }
    case 'assistant':
      handleAssistantMessage(data, blocks, setIsAgentTurn, eventTimestamp, sourceEvent);
      break;
    case 'user':
      handleToolResults(data, blocks, eventTimestamp);
      break;
    case 'result':
      handleResult(
        data,
        blocks,
        appendMessage,
        setIsAgentTurn,
        eventTimestamp,
        interrupted,
        sourceEvent,
      );
      break;
    case 'system':
      handleSystemMessage(data, blocks, eventTimestamp, sourceEvent);
      break;
    case 'control_request':
      handleControlRequest(data, blocks, eventTimestamp, eventOrigin, sourceEvent);
      break;
    case 'control_response':
      handleControlResponse(data, blocks);
      break;
  }
}

function handleAssistantMessage(
  msg: Record<string, unknown>,
  blocks: BlockManager,
  setIsAgentTurn: (v: boolean) => void,
  eventTimestamp: number,
  sourceEvent?: AxonEvent,
): void {
  const message = msg.message as Record<string, unknown> | undefined;

  // The message names the model that served it, which is how a `/model`
  // switch — applied inside the CLI, never written back to the agent record —
  // becomes visible to the chat. Read before the content guard below: the
  // model rides on the message itself and does not depend on the frame
  // carrying renderable blocks.
  blocks.noteModel(claudeAssistantModel(msg));

  const content = (message?.content ?? msg.content) as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(content)) return;

  setIsAgentTurn(true);

  for (const block of content) {
    if (block.type === 'thinking') {
      blocks.finalizeThinking(eventTimestamp);
      blocks.pushBlock({
        type: 'thinking',
        id: nextBlockId('think'),
        text: (block.thinking as string) ?? '',
        duration: null,
        isActive: false,
        sourceEvent,
      });
    } else if (block.type === 'text') {
      blocks.finalizeThinking(eventTimestamp);
      blocks.pushBlock({
        type: 'text',
        id: nextBlockId('txt'),
        text: (block.text as string) ?? '',
        sourceEvent,
      });
    } else if (block.type === 'tool_use') {
      blocks.finalizeThinking(eventTimestamp);
      const toolName = (block.name as string) ?? UNTITLED_TOOL_CALL;

      if (toolName === 'TodoWrite') {
        handleTodoWrite(block, blocks, sourceEvent);
        continue;
      }

      blocks.pushBlock({
        type: 'tool_call',
        id: nextBlockId('tc'),
        toolCallId: (block.id as string) ?? '',
        title: toolName,
        kind: inferToolKind(toolName),
        status: 'in_progress',
        locations: [],
        content: [],
        rawInput: (block.input as Record<string, unknown> | null) ?? null,
        rawOutput: null,
        startedAt: eventTimestamp,
        duration: null,
        sourceEvent,
        extra: { toolName },
      });
    }
  }
}

function handleTodoWrite(
  block: Record<string, unknown>,
  blocks: BlockManager,
  sourceEvent?: AxonEvent,
): void {
  const input = block.input as Record<string, unknown> | undefined;
  const todos = input?.todos as Array<Record<string, unknown>> | undefined;
  if (!todos) return;

  const entries: PlanEntry[] = todos.map((t, i) => ({
    id: typeof t.id === 'string' ? t.id : String(i),
    content: (t.content as string) ?? '',
    status: (t.status as PlanEntry['status']) ?? 'pending',
    priority: null,
  }));

  const isMerge = input?.merge !== false;

  if (isMerge) {
    const base = blocks.planEntriesRef.current;
    const merged = mergePlanEntries(base, entries);
    blocks.planEntriesRef.current = merged;
  } else {
    blocks.planEntriesRef.current = entries;
  }

  const accumulated = blocks.planEntriesRef.current;
  const existingPlan = blocks.blocksRef.current.find((b) => b.type === 'plan');
  if (existingPlan) {
    blocks.updateBlocks((prev) =>
      prev.map((b) => (b.type === 'plan' ? { ...b, entries: accumulated } : b)),
    );
  } else {
    blocks.pushBlock({
      type: 'plan',
      id: nextBlockId('plan'),
      entries: accumulated,
      sourceEvent,
    });
  }
}

function handleToolResults(
  msg: Record<string, unknown>,
  blocks: BlockManager,
  eventTimestamp: number,
): void {
  const message = msg.message as Record<string, unknown> | undefined;
  const content = (message?.content ?? msg.content) as Array<Record<string, unknown>> | undefined;
  if (!Array.isArray(content)) return;

  // Structured detail the tool reports alongside its text output: whether the
  // call handed off to a background task, and what a file-editing tool did on
  // disk (see `parseWriteTarget`).
  const toolUseResult = msg.tool_use_result as Record<string, unknown> | undefined;
  const backgroundTaskId = toolUseResult?.backgroundTaskId as string | undefined;

  for (const block of content) {
    if (block.type !== 'tool_result') continue;

    const toolUseId = block.tool_use_id as string;
    const resultContent = block.content as unknown;
    let outputText = '';
    if (typeof resultContent === 'string') {
      outputText = resultContent;
    } else if (Array.isArray(resultContent)) {
      outputText = resultContent
        .filter((c: Record<string, unknown>) => c.type === 'text')
        .map((c: Record<string, unknown>) => c.text as string)
        .join('\n');
    }
    const isError = block.is_error === true;

    // If the tool_use_result payload explicitly indicates a background task, keep
    // the tool_call as in_progress. We only check the payload's backgroundTaskId —
    // NOT the block's extra.backgroundTaskId, because task_started fires for both
    // foreground and background tasks and would cause false positives.
    const hasBackgroundTask = !!backgroundTaskId;

    blocks.updateBlocks((prev) =>
      prev.map((b) => {
        if (b.type !== 'tool_call' || b.toolCallId !== toolUseId) return b;
        // Sticky cancel: when the user interrupts mid-tool, Claude follows up
        // with a synthetic tool_result marked `is_error` ("The user doesn't
        // want to proceed…"). The tool was already marked cancelled at
        // interrupt time — don't let that rejection flip it back to "failed".
        if (b.status === 'cancelled') return b;
        const isFinishing = b.status !== 'completed' && b.status !== 'failed';
        const diffContent = !isError ? buildDiffContent(b, toolUseResult, outputText) : null;

        if (hasBackgroundTask && !isError) {
          // Background task is running — keep in_progress, store taskId
          return {
            ...b,
            rawOutput: b.rawOutput,
            content:
              diffContent ??
              (outputText ? [{ type: 'content' as const, text: outputText }] : b.content),
            status: 'in_progress' as const,
            extra: { ...b.extra, backgroundTaskId: backgroundTaskId ?? b.extra?.backgroundTaskId },
          };
        }

        return {
          ...b,
          rawOutput: b.rawOutput,
          content:
            diffContent ??
            (outputText ? [{ type: 'content' as const, text: outputText }] : b.content),
          status: isError ? ('failed' as const) : ('completed' as const),
          duration: isFinishing ? durationSecs(b.startedAt, eventTimestamp) : b.duration,
        };
      }),
    );
  }
}

function handleResult(
  msg: Record<string, unknown>,
  blocks: BlockManager,
  appendMessage: (msg: ChatItem) => void,
  setIsAgentTurn: (v: boolean) => void,
  eventTimestamp: number,
  interrupted: boolean,
  sourceEvent?: AxonEvent,
): void {
  // A user interrupt arriving before the result re-frames the turn as
  // cancelled (not an execution error). When the interrupt arrives *after*
  // the result, useSDKStream relabels the flushed message retroactively.
  const isError = !interrupted && msg.is_error === true;
  const stopReason = interrupted
    ? 'cancelled'
    : ((msg.stop_reason as string) ??
      (msg.is_error === true ? (msg.subtype as string) : undefined));
  // TODO(cumulative-turn-usage): claude-code reports cost and num_turns as
  // session-cumulative totals, so per-message stats overstate the turn.
  const cost = msg.total_cost_usd as number | undefined;
  const numTurns = msg.num_turns as number | undefined;
  const durationMs = msg.duration_ms as number | undefined;

  // If the result carries a text message (e.g. "Unknown command: /foo") but no
  // content blocks were streamed, push a text block so the message is visible.
  // Check for actual content blocks (text/tool_call/thinking), not system blocks
  // like system_init which are always present after init.
  const resultText = typeof msg.result === 'string' ? msg.result : null;
  const hasContentBlocks = blocks.blocksRef.current.some(
    (b) => b.type === 'text' || b.type === 'tool_call' || b.type === 'thinking',
  );
  if (resultText && !hasContentBlocks) {
    blocks.pushBlock({
      type: 'text',
      id: nextBlockId('txt'),
      text: resultText,
      sourceEvent,
    });
  }

  // If there are in-progress background tasks, don't flush yet — accumulate
  // result metadata and keep blocks live. The final flush happens when the
  // background task completes and the completion turn finishes.
  if (blocks.hasPendingBackgroundTasks() && !isError) {
    const prev = blocks.pendingResultRef.current;
    // Cost and num_turns are session-cumulative, so the latest report wins
    // (summing would double-count every earlier turn, and a session restart
    // resets the counters, so a max would pin stale values). Durations are
    // per-turn and sum across the parts that make up this visual turn.
    blocks.pendingResultRef.current = {
      cost: cost ?? prev?.cost ?? 0,
      numTurns: numTurns ?? prev?.numTurns ?? 0,
      durationMs: (prev?.durationMs ?? 0) + (durationMs ?? 0),
      stopReason: stopReason ?? prev?.stopReason,
    };
    // Keep isAgentTurn true so blocks stay visible as the "current turn"
    return;
  }

  // Merge any accumulated result metadata from earlier turns: latest
  // cumulative report wins for cost/turns, durations sum (see above).
  const prev = blocks.pendingResultRef.current;
  blocks.pendingResultRef.current = null;
  const totalCost = cost ?? prev?.cost ?? 0;
  const totalTurns = numTurns ?? prev?.numTurns ?? 0;
  const totalDuration = (prev?.durationMs ?? 0) + (durationMs ?? 0);

  if (interrupted) {
    blocks.updateBlocks((b) => cancelInFlightTools(b, eventTimestamp));
  }

  const flushed = blocks.flushToMessage(
    {
      stopReason: interrupted ? 'cancelled' : (stopReason ?? prev?.stopReason ?? undefined),
      isError,
      cost: totalCost || undefined,
      numTurns: totalTurns || undefined,
      durationMs: totalDuration || undefined,
      timestamp: eventTimestamp,
      sourceEvent,
    },
    eventTimestamp,
  );
  if (flushed) appendMessage(flushed);
  setIsAgentTurn(false);
}

function handleSystemMessage(
  msg: Record<string, unknown>,
  blocks: BlockManager,
  eventTimestamp: number,
  sourceEvent?: AxonEvent,
): void {
  const subtype = msg.subtype as string;
  switch (subtype) {
    case 'init': {
      const tools = (msg.tools as string[]) ?? [];
      const agents = (msg.agents as string[]) ?? [];
      const skills = (msg.skills as string[]) ?? [];
      const mcpServers = (msg.mcp_servers as Array<{ name: string; status: string }>) ?? [];
      const permMode = (msg.permissionMode as string) ?? 'default';
      const slashCommands = (msg.slash_commands as string[]) ?? [];
      const model = (msg.model as string) ?? null;
      const version = (msg.claude_code_version as string) ?? undefined;

      const extensions: ClaudeInitExtensions = {
        protocol: 'claude',
        tools,
        agents,
        skills,
        mcpServers,
        permissionMode: permMode,
        apiKeySource: (msg.apiKeySource as string) ?? undefined,
        cwd: (msg.cwd as string) ?? undefined,
        sessionId: (msg.session_id as string) ?? undefined,
        claudeCodeVersion: version,
        fastModeState: (msg.fast_mode_state as string) ?? undefined,
      };
      blocks.pushBlock({
        type: 'system_init',
        id: nextBlockId('init'),
        agentName: 'Claude Code',
        agentVersion: version ?? null,
        model,
        commands: slashCommands,
        extensions,
        sourceEvent,
        extra: { ...msg },
      });
      break;
    }
    case 'task_started': {
      const taskId = (msg.task_id as string) ?? '';
      const toolUseId = msg.tool_use_id as string | undefined;
      const description = (msg.description as string) ?? '';
      const taskType = typeof msg.task_type === 'string' ? msg.task_type : undefined;

      // If this task is linked to a tool_call via tool_use_id, merge into the tool_call
      if (toolUseId) {
        const linked = blocks.blocksRef.current.find(
          (b) => b.type === 'tool_call' && b.toolCallId === toolUseId,
        );
        if (linked) {
          blocks.updateBlocks((prev) =>
            prev.map((b) =>
              b.type === 'tool_call' && b.toolCallId === toolUseId
                ? {
                    ...b,
                    status: 'in_progress' as const,
                    extra: {
                      ...b.extra,
                      backgroundTaskId: taskId,
                      backgroundDescription: description,
                      backgroundTaskType: taskType ?? b.extra?.backgroundTaskType,
                    },
                  }
                : b,
            ),
          );
          break;
        }
      }

      blocks.pushBlock({
        type: 'task',
        id: nextBlockId('task'),
        taskId,
        description,
        status: 'started',
        sourceEvent,
      });
      break;
    }
    case 'task_progress': {
      const taskId = msg.task_id as string;
      const description = msg.description as string;
      const taskUsage = msg.usage as Record<string, number> | undefined;
      blocks.updateBlocks((prev) =>
        prev.map((b) =>
          b.type === 'task' && b.taskId === taskId
            ? { ...b, status: 'in_progress' as const, description, toolUses: taskUsage?.tool_uses }
            : b,
        ),
      );
      break;
    }
    case 'task_notification':
    case 'task_completed':
    case 'task_updated': {
      const taskId = msg.task_id as string;
      const patch = msg.patch as Record<string, unknown> | undefined;
      const summary = msg.summary as string;
      const status =
        (patch?.status as string | undefined) ??
        (msg.status as string | undefined) ??
        (subtype === 'task_completed' ? 'completed' : undefined);
      if (taskId) {
        // Check if this task is linked to a tool_call block
        const linkedToolCall = blocks.blocksRef.current.find(
          (b) => b.type === 'tool_call' && b.extra?.backgroundTaskId === taskId,
        );
        if (linkedToolCall) {
          const isTerminal = status === 'completed' || status === 'failed' || status === 'stopped';
          blocks.updateBlocks((prev) =>
            prev.map((b) => {
              if (b.type !== 'tool_call' || b.extra?.backgroundTaskId !== taskId) return b;
              if (!isTerminal) return b;
              return {
                ...b,
                status: status === 'completed' ? ('completed' as const) : ('failed' as const),
                extra: { ...b.extra, backgroundSummary: summary },
                duration: durationSecs(b.startedAt, eventTimestamp),
              };
            }),
          );
          break;
        }

        const existing = blocks.blocksRef.current.find(
          (b) => b.type === 'task' && b.taskId === taskId,
        );
        if (existing) {
          blocks.updateBlocks((prev) =>
            prev.map((b) =>
              b.type === 'task' && b.taskId === taskId
                ? {
                    ...b,
                    status: (status as TaskBlock['status']) ?? b.status,
                    summary: summary ?? b.summary,
                  }
                : b,
            ),
          );
        } else {
          blocks.pushBlock({
            type: 'task',
            id: nextBlockId('task'),
            taskId,
            description: (msg.description as string) ?? '',
            status: (status as TaskBlock['status']) ?? 'completed',
            summary,
            sourceEvent,
          });
        }
      }
      break;
    }
  }
}

function handleControlRequest(
  msg: Record<string, unknown>,
  blocks: BlockManager,
  eventTimestamp: number,
  eventOrigin?: string,
  sourceEvent?: AxonEvent,
): void {
  const requestId = (msg.request_id as string) ?? '';
  const request = (msg.request as Record<string, unknown>) ?? {};
  const requestSubtype = (request.subtype as string) ?? 'permission';

  if (requestSubtype === 'interrupt' || requestSubtype === 'initialize') return;
  if (eventOrigin === 'USER_EVENT') return;

  const toolName = typeof request.tool_name === 'string' ? request.tool_name : null;
  const toolInput = (request.input as Record<string, unknown>) ?? null;
  const toolUseId = typeof request.tool_use_id === 'string' ? request.tool_use_id : null;

  const block: ControlRequestBlock = {
    type: 'control_request',
    id: nextBlockId('ctrl'),
    requestId,
    toolName,
    toolInput,
    toolUseId,
    requestSubtype,
    timestamp: eventTimestamp,
    responded: false,
    rawEvent: msg,
    sourceEvent,
  };
  blocks.pushBlock(block);
}

/**
 * Pull the `request_id` and `updatedInput` from a Claude `control_response`
 * payload. Shared by the live-buffer resolver below and the flushed-message
 * fallback in {@link useSDKStream}, so both paths see the same parse.
 */
export function parseControlResponse(msg: Record<string, unknown>): {
  requestId: string;
  updatedInput: Record<string, unknown> | null;
} {
  const outerResponse = msg.response as Record<string, unknown> | undefined;
  const requestId = (msg.request_id as string) ?? (outerResponse?.request_id as string) ?? '';

  // Claude wraps the client payload as `{ type: 'control_response',
  // response: { subtype, request_id, response: { behavior, updatedInput } } }`.
  // The `updatedInput` carries the AskUserQuestion `answers` map keyed by
  // question text.
  const innerResponse = outerResponse?.response as Record<string, unknown> | undefined;
  const updatedInput =
    innerResponse && typeof innerResponse.updatedInput === 'object' && innerResponse.updatedInput
      ? (innerResponse.updatedInput as Record<string, unknown>)
      : null;

  return { requestId, updatedInput };
}

function handleControlResponse(msg: Record<string, unknown>, blocks: BlockManager): void {
  const { requestId, updatedInput } = parseControlResponse(msg);
  if (!requestId) return;

  // Merge `updatedInput` back onto the originating ControlRequestBlock so the
  // UI can render the user's selections without keeping local state.
  blocks.updateBlocks((prev) =>
    prev.map((b) => {
      if (b.type !== 'control_request' || b.requestId !== requestId) return b;
      const mergedInput = updatedInput ? { ...(b.toolInput ?? {}), ...updatedInput } : b.toolInput;
      return { ...b, responded: true, toolInput: mergedInput };
    }),
  );
}

/**
 * Handle Claude streaming events (content_block_start/delta/stop).
 */
function handleStreamEvent(
  event: Record<string, unknown>,
  blocks: BlockManager,
  setIsAgentTurn: (v: boolean) => void,
  eventTimestamp: number,
  sourceEvent?: AxonEvent,
): void {
  const eventType = event.type as string;

  switch (eventType) {
    case 'content_block_start': {
      const contentBlock = event.content_block as Record<string, unknown>;
      if (!contentBlock) break;
      const blockType = contentBlock.type as string;

      if (blockType === 'thinking') {
        blocks.finalizeThinking(eventTimestamp);
        blocks.thinkingStartRef.current = eventTimestamp;
        blocks.pushBlock({
          type: 'thinking',
          id: nextBlockId('think'),
          text: '',
          duration: null,
          isActive: true,
          sourceEvent,
        });
        setIsAgentTurn(true);
      } else if (blockType === 'text') {
        blocks.finalizeThinking(eventTimestamp);
        blocks.pushBlock({
          type: 'text',
          id: nextBlockId('txt'),
          text: '',
          sourceEvent,
        });
        setIsAgentTurn(true);
      } else if (blockType === 'tool_use') {
        blocks.finalizeThinking(eventTimestamp);
        const toolName = (contentBlock.name as string) ?? UNTITLED_TOOL_CALL;

        if (toolName === 'TodoWrite') {
          blocks.streamingTodoInputRef.current = '';
          setIsAgentTurn(true);
          break;
        }

        blocks.pushBlock({
          type: 'tool_call',
          id: nextBlockId('tc'),
          toolCallId: (contentBlock.id as string) ?? '',
          title: toolName,
          kind: inferToolKind(toolName),
          status: 'pending',
          locations: [],
          content: [],
          rawInput: null,
          rawOutput: null,
          startedAt: eventTimestamp,
          duration: null,
          sourceEvent,
          extra: { toolName },
        });
        setIsAgentTurn(true);
      }
      break;
    }

    case 'content_block_delta': {
      const delta = event.delta as Record<string, unknown>;
      if (!delta) break;
      const deltaType = delta.type as string;

      if (deltaType === 'thinking_delta') {
        const thinking = delta.thinking as string;
        if (thinking) {
          blocks.updateBlocks((prev) => {
            const last = prev[prev.length - 1];
            if (last?.type === 'thinking' && last.isActive) {
              return [...prev.slice(0, -1), { ...last, text: last.text + thinking }];
            }
            return prev;
          });
        }
      } else if (deltaType === 'text_delta') {
        const text = delta.text as string;
        if (text) {
          blocks.updateBlocks((prev) => {
            const last = prev[prev.length - 1];
            if (last?.type === 'text') {
              return [...prev.slice(0, -1), { ...last, text: last.text + text }];
            }
            return prev;
          });
        }
      } else if (deltaType === 'input_json_delta') {
        const partialJson = delta.partial_json as string;
        if (partialJson && blocks.streamingTodoInputRef.current !== null) {
          blocks.streamingTodoInputRef.current += partialJson;
        }
      }
      break;
    }

    case 'content_block_stop': {
      if (blocks.streamingTodoInputRef.current !== null) {
        try {
          const parsed = JSON.parse(blocks.streamingTodoInputRef.current) as Record<
            string,
            unknown
          >;
          handleTodoWrite({ input: parsed }, blocks, sourceEvent);
        } catch {
          // Incomplete JSON — ignore
        }
        blocks.streamingTodoInputRef.current = null;
      }

      const lastBlock = blocks.lastBlock();
      if (lastBlock?.type === 'thinking' && lastBlock.isActive) {
        blocks.finalizeThinking(eventTimestamp);
      }
      break;
    }

    case 'message_start':
      setIsAgentTurn(true);
      break;
  }
}

function mergePlanEntries(existing: PlanEntry[], incoming: PlanEntry[]): PlanEntry[] {
  const map = new Map<string, PlanEntry>();
  for (const entry of existing) {
    const key = entry.id ?? entry.content;
    map.set(key, entry);
  }
  for (const entry of incoming) {
    const key = entry.id ?? entry.content;
    const prev = map.get(key);
    map.set(key, prev ? { ...prev, ...entry } : entry);
  }
  return Array.from(map.values());
}

/**
 * What a `Write` call did to the file it targeted, read off the tool's result
 * rather than guessed from its input.
 *
 * The input is identical whether the write created a file or replaced one, so
 * it can never tell the two apart. The result can: Claude Code's
 * `tool_use_result` carries `type: 'create' | 'update'` and, for an update, the
 * file's `originalFile` contents. When that payload is missing, the result text
 * is the only other witness, and it distinguishes the two cases but carries no
 * contents. When neither says which happened, the answer stays unknown — better
 * than assuming "new file" and billing every line as an addition.
 */
interface WriteTarget {
  /** True when the write replaced an existing file. Null when nothing in the result says. */
  existed: boolean | null;
  /** The file's contents before the write, when the result carried them. */
  originalText: string | null;
}

const CREATED_RESULT_TEXT = /^File created successfully at:/;
const UPDATED_RESULT_TEXT = /^The file .* has been updated/;

function parseWriteTarget(
  path: string,
  toolUseResult: Record<string, unknown> | undefined,
  outputText: string,
): WriteTarget {
  // One `tool_use_result` accompanies the whole message, so it only speaks for
  // this tool call when its file path agrees (or when it names no path at all).
  const filePath = toolUseResult?.filePath;
  const forThisCall =
    toolUseResult !== undefined && (typeof filePath !== 'string' || filePath === path);
  const type = forThisCall ? toolUseResult.type : undefined;
  if (type === 'update') {
    const originalFile = toolUseResult?.originalFile;
    return { existed: true, originalText: typeof originalFile === 'string' ? originalFile : null };
  }
  if (type === 'create') return { existed: false, originalText: null };

  if (CREATED_RESULT_TEXT.test(outputText)) return { existed: false, originalText: null };
  if (UPDATED_RESULT_TEXT.test(outputText)) return { existed: true, originalText: null };
  return { existed: null, originalText: null };
}

/**
 * For file-editing tools (Write, Edit, StrReplace), build a diff ContentItem
 * from the tool's rawInput so the user sees a unified diff instead of the
 * plain "File created/updated" success text.
 */
function buildDiffContent(
  tc: ToolCallBlock,
  toolUseResult: Record<string, unknown> | undefined,
  outputText: string,
): ContentItem[] | null {
  const input = tc.rawInput ?? null;
  if (!input) return null;

  // Write: { file_path, content } or { file_path, contents } — new or overwritten file
  const writeContent =
    typeof input.content === 'string'
      ? input.content
      : typeof input.contents === 'string'
        ? input.contents
        : undefined;
  if (tc.title === 'Write' && typeof input.file_path === 'string' && writeContent !== undefined) {
    const target = parseWriteTarget(input.file_path, toolUseResult, outputText);
    // A file we know was new diffs against nothing; one whose previous contents
    // we hold diffs against those. Anything else replaced contents we can't
    // reconstruct, and says so instead of diffing against an empty file.
    const diff: DiffContent =
      target.existed === false
        ? { path: input.file_path, oldText: null, newText: writeContent }
        : target.originalText !== null
          ? { path: input.file_path, oldText: target.originalText, newText: writeContent }
          : {
              path: input.file_path,
              oldText: null,
              newText: writeContent,
              originalUnavailable: true,
            };
    return [{ type: 'diff' as const, diff }];
  }

  // Edit: { file_path, old_string, new_string } — targeted in-place edit
  if (
    tc.title === 'Edit' &&
    typeof input.file_path === 'string' &&
    typeof input.old_string === 'string' &&
    typeof input.new_string === 'string'
  ) {
    return [
      {
        type: 'diff' as const,
        diff: { path: input.file_path, oldText: input.old_string, newText: input.new_string },
      },
    ];
  }

  // StrReplace: { path, old_string, new_string }
  if (
    tc.title === 'StrReplace' &&
    typeof input.path === 'string' &&
    typeof input.old_string === 'string' &&
    typeof input.new_string === 'string'
  ) {
    return [
      {
        type: 'diff' as const,
        diff: { path: input.path, oldText: input.old_string, newText: input.new_string },
      },
    ];
  }

  return null;
}
